import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface ToolboxItemData {
  id: string;
  typeId: string;
  title: string;
  category: string;
  defaultDescription: string | null;
  iconName: string | null;
  sortOrder: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateToolboxItemData {
  typeId: string;
  title: string;
  category: string;
  defaultDescription?: string;
  iconName?: string;
  sortOrder?: number;
}

export function useToolboxItems() {
  const queryClient = useQueryClient();

  const { data: items = [], isLoading, isError } = useQuery({
    queryKey: ["toolbox-items"],
    queryFn: async () => {
      const response = await fetch("/api/toolbox-items");
      if (!response.ok) {
        throw new Error("Failed to fetch toolbox items");
      }
      return response.json() as Promise<ToolboxItemData[]>;
    },
    staleTime: 1000 * 60 * 5,
  });

  const createItem = useMutation({
    mutationFn: async (data: CreateToolboxItemData) => {
      const response = await fetch("/api/toolbox-items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to create item");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["toolbox-items"] });
    },
  });

  const updateItem = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CreateToolboxItemData> & { id: string }) => {
      const response = await fetch(`/api/toolbox-items/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        throw new Error("Failed to update item");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["toolbox-items"] });
    },
  });

  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/toolbox-items/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete item");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["toolbox-items"] });
    },
  });

  const stageItems = items.filter(i => i.category === "stage");
  const painItems = items.filter(i => i.category === "pain");
  const interventionItems = items.filter(i => i.category === "intervention");

  return {
    items,
    stageItems,
    painItems,
    interventionItems,
    isLoading,
    isError,
    createItem: createItem.mutate,
    createItemAsync: createItem.mutateAsync,
    updateItem: updateItem.mutate,
    updateItemAsync: updateItem.mutateAsync,
    deleteItem: deleteItem.mutate,
    deleteItemAsync: deleteItem.mutateAsync,
    isCreating: createItem.isPending,
    isUpdating: updateItem.isPending,
    isDeleting: deleteItem.isPending,
  };
}
